import { useEffect, useState } from 'react';
import { array, element, number, shape, string, bool } from 'prop-types';

import {
	Collapse,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Tooltip,
	Badge,
	Box
} from 'components/mui';
import { ExpandLess, ExpandMore } from 'components/icons';
import { useTheme, alpha } from '@mui/material/styles';
import ListMenu from './ListMenu';
import styles from '../styles/cantab.module.css';
import { APP_CAN_APPS_V3, CAN_APPS } from 'constants/appNames';
import { getCANsenseColour } from '../data/getCANsenseColour';

/**
 * @component ListApp
 * @param {Object} props
 * @param {array} props.menu
 * @param {Object} props.alertsData - Centralized alerts data passed down from parent
 * @returns {JSX.Element}
 */
function ListApp({ menu, menuOpen, setMenuOpen, setMenuClose, activeTab, alertsData }) {
	const theme = useTheme();
	const [open, setOpen] = useState(false);
	// Determine if this menu item should show alerts
	const shouldShowAlerts = menu.name?.toLowerCase()?.includes('cansense') ||
	                        menu.name?.toLowerCase()?.includes('sense');

	// Only use alert data if this is a CANsense menu item AND alertsData is provided
	const {
		alertStatus = null,
		hasActiveAlerts = false,
		activeAlertCount = 0,
		newAlertCount = 0,
		acknowledgedCount = 0,
		resolvedCount = 0,
		highestSeverity = null,
		isConnected = false,
		connectionState = 'disconnected',
		loading = false,
		error = null
	} = (shouldShowAlerts && alertsData) ? alertsData : {};

	// Calculate total alert count for legacy compatibility
	const alertCount = activeAlertCount;

	const status = (() => {
		if (!isConnected) return 'inactive';

		// FIXED: Only consider active alerts for status (new + acknowledged)
		if (newAlertCount > 0) return 'firing';        // Red light - needs immediate attention
		if (acknowledgedCount > 0) return 'acknowledged'; // Amber light - being worked on
		return 'ok';                                    // Green light - all clear
	})();

	const menuContainsActiveTab = (tabs) => {
		if (!tabs) return false;
		return tabs.some((tab) => {
			if (!tab.url) return false;
			const tabUrl = tab.type === APP_CAN_APPS_V3 ? `/${APP_CAN_APPS_V3}${tab.url}` : tab.url;
			if (activeTab === tabUrl) return true;
			if (tab.subtabs) {
				return tab.subtabs.some((subtab) => {
					if (!subtab.url) return false;
					const subtabUrl = subtab.type === APP_CAN_APPS_V3 ? `/${APP_CAN_APPS_V3}${subtab.url}` : subtab.url;
					return activeTab === subtabUrl;
				});
			}
			return false;
		});
	};

	const onOpen = () => {
		setOpen(!open);
	};

	useEffect(() => {
		if (!menuOpen) {
			setOpen(false);
		} else {
			setOpen(menuContainsActiveTab(menu.tabs));
		}
	}, [menu.name, menuOpen]);

	// Helper functions for CANsense styling using MUI theme
	const getAlertStatusText = () => {
	if (!shouldShowAlerts || loading) return '';

	// FIXED: Use isConnected flag to determine connection state
	// instead of relying on connectionState variable
	if (!isConnected) return 'Connection lost';

	// Only check connectionState if isConnected is true but in a connecting state
	if (isConnected && connectionState === 'connecting') return 'Connecting...';

	// FIXED: Only show new alerts as urgent, acknowledged as secondary
	if (newAlertCount > 0) {
		return `${newAlertCount} new alert${newAlertCount > 1 ? 's' : ''}`;
	}
	if (acknowledgedCount > 0) {
		return `${acknowledgedCount} acknowledged`;
	}

	// If no active alerts, don't show any secondary text
	return '';
	};

	const getBackgroundColor = () => {
		if (!shouldShowAlerts || !menuOpen) return 'transparent';

		// Show different colors based on connection and alert state using MUI theme
		if (connectionState === 'disconnected' || connectionState === 'error') {
			return alpha(theme.palette.grey[500], 0.08);
		}

		// FIXED: Only use active alerts for background color
		if (newAlertCount > 0) {
			switch (highestSeverity) {
				case 'critical': return alpha(theme.palette.error.dark, 0.08);
				case 'error': return alpha(theme.palette.error.main, 0.08);
				case 'warning': return alpha(theme.palette.warning.main, 0.08);
				case 'info': return alpha(theme.palette.info.main, 0.08);
				default: return alpha(theme.palette.error.main, 0.08);
			}
		}
		if (acknowledgedCount > 0) return alpha(theme.palette.warning.main, 0.05);

		// If only resolved alerts, no special background
		return 'transparent';
	};

	const getHoverBackgroundColor = () => {
		if (!shouldShowAlerts) return undefined;
		if (newAlertCount > 0) return alpha(theme.palette.error.main, 0.12);
		if (acknowledgedCount > 0) return alpha(theme.palette.warning.main, 0.1);
		return undefined;
	};

	const getBorderLeft = () => {
		if (!shouldShowAlerts || !activeAlertCount) return 'none';
		return `${theme.spacing(0.375)} solid ${getCANsenseColour(status, highestSeverity, theme)}`;
	};

	const getSecondaryTextColor = () => {
		if (connectionState === 'disconnected' || connectionState === 'error') {
			return theme.palette.grey[500];
		}
		if (newAlertCount > 0) {
			return theme.palette.error.main;
		}
		if (acknowledgedCount > 0) {
			return theme.palette.warning.main;
		}
		return theme.palette.text.secondary;
	};

	// Enhanced icon with alert badge for CANsense using MUI standards
	const renderMenuIcon = () => {
		if (!shouldShowAlerts) {
			return <ListItemIcon className={styles.la_list_icon}>{menu.icon}</ListItemIcon>;
		}

		// Show connection status for disconnected state
		// FIXED: Use isConnected flag consistently
		if (!isConnected) {
			return (
			<ListItemIcon className={styles.la_list_icon}>
				<Badge
				badgeContent="!"
				color="error"
				sx={{
					'& .MuiBadge-badge': {
					fontSize: theme.typography.caption.fontSize,
					minWidth: theme.spacing(2),
					height: theme.spacing(2),
					backgroundColor: theme.palette.grey[500],
					color: theme.palette.common.white,
					fontWeight: theme.typography.fontWeightBold,
					right: theme.spacing(-0.375),
					top: theme.spacing(-0.375),
					}
				}}
				>
				{menu.icon}
				</Badge>
			</ListItemIcon>
			);
		}

		// FIXED: Only show badge for active alerts (new + acknowledged)
		const displayCount = activeAlertCount > 0 ? activeAlertCount : null;

		if (!displayCount && !loading) {
			// No active alerts and connected - show green icon
			return (
				<ListItemIcon className={styles.la_list_icon}>
					<Box sx={{ color: theme.palette.success.main, display: 'flex' }}>
						{menu.icon}
					</Box>
				</ListItemIcon>
			);
		}

		return (
			<ListItemIcon className={styles.la_list_icon}>
				<Badge
					badgeContent={displayCount}
					color={newAlertCount > 0 ? "error" : "warning"}
					max={99}
					sx={{
						'& .MuiBadge-badge': {
							fontSize: theme.typography.caption.fontSize,
							minWidth: theme.spacing(2),
							height: theme.spacing(2),
							backgroundColor: newAlertCount > 0
								? theme.palette.error.main
								: theme.palette.warning.main,
							color: theme.palette.common.white,
							fontWeight: theme.typography.fontWeightBold,
							right: theme.spacing(-0.375),
							top: theme.spacing(-0.375),
						}
					}}
				>
					{menu.icon}
				</Badge>
			</ListItemIcon>
		);
	};

	// Enhanced tooltip for CANsense
	const getTooltipTitle = () => {
	if (!menuOpen) {
		if (shouldShowAlerts) {
		if (!isConnected) return `${menu.name} - Disconnected`;
		if (connectionState === 'connecting') return `${menu.name} - Connecting...`;
		if (activeAlertCount > 0) return `${menu.name} - ${getAlertStatusText()}`;
		}
		return menu.name;
	}
	return '';
	};

	// MUI keyframes animation for critical alerts
	const criticalPulseAnimation = {
		'@keyframes criticalPulse': {
			'0%': {
				opacity: 1,
				transform: 'scale(1)'
			},
			'50%': {
				opacity: 0.8,
				transform: 'scale(1.02)'
			},
			'100%': {
				opacity: 1,
				transform: 'scale(1)'
			},
		}
	};

	return (
		<Box>
			<ListItemButton
				data-tour={
					menu.tabs?.some((t) => t.url?.includes('canpark-admin'))
						? 'canpark-menu-group'
						: menu.name === CAN_APPS
							? 'canapps-menu-group'
							: undefined
				}
				className={styles.la_list_btn}
				onClick={() => {
					setMenuOpen();
					onOpen();
				}}
				sx={{
					backgroundColor: getBackgroundColor(),
					borderLeft: getBorderLeft(),
					transition: theme.transitions.create(
						['background-color', 'border-left', 'transform'],
						{
							duration: theme.transitions.duration.standard,
							easing: theme.transitions.easing.easeInOut,
						}
					),
					'&:hover': {
						backgroundColor: getHoverBackgroundColor(),
					},
					// FIXED: Only pulse for NEW critical alerts, not acknowledged ones
					...(shouldShowAlerts && newAlertCount > 0 && highestSeverity === 'critical' && {
						...criticalPulseAnimation,
						animation: 'criticalPulse 2s infinite',
					}),
				}}
			>
				<Tooltip
					title={getTooltipTitle()}
					placement="right"
					arrow
					enterDelay={theme.transitions.duration.standard}
					leaveDelay={theme.transitions.duration.shorter}
				>
					{renderMenuIcon()}
				</Tooltip>

				{/* Enhanced text with alert status for CANsense when menu is open */}
				{shouldShowAlerts && menuOpen ? (
					<ListItemText
						className={styles.la_list_text}
						primary={menu.name}
						secondary={getAlertStatusText()}
						primaryTypographyProps={{
							variant: 'body2',
							fontWeight: theme.typography.fontWeightMedium,
						}}
						secondaryTypographyProps={{
							variant: 'caption',
							color: getSecondaryTextColor(),
							fontWeight: newAlertCount > 0
								? theme.typography.fontWeightMedium
								: theme.typography.fontWeightRegular,
							sx: {
								mt: theme.spacing(0.25),
								display: 'block',
							}
						}}
					/>
				) : (
					<ListItemText
						className={styles.la_list_text}
						primary={menu.name}
						primaryTypographyProps={{
							variant: 'body2',
							fontWeight: theme.typography.fontWeightMedium,
						}}
					/>
				)}

				<Box
					sx={{
						transition: theme.transitions.create('transform', {
							duration: theme.transitions.duration.shortest,
						}),
						transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
					}}
				>
					{open ? (
						<ExpandLess className={styles.la_list_icon_open} />
					) : (
						<ExpandMore className={styles.la_list_icon_open} />
					)}
				</Box>
			</ListItemButton>

			<Collapse
				in={open}
				timeout={{
					enter: theme.transitions.duration.enteringScreen,
					exit: theme.transitions.duration.leavingScreen,
				}}
				unmountOnExit
			>
				{(menu.tabs ?? []).map((tab, index) => (
					<ListMenu
						key={index}
						open={open}
						tab={tab}
						setMenuClose={setMenuClose}
						activeTab={activeTab}
					/>
				))}
			</Collapse>
		</Box>
	);
}

ListApp.propTypes = {
	menu: shape({ id: number, name: string, tabs: array, icon: element }),
	alertsData: shape({
		alerts: array,
		alertStatus: shape({
			alerts: shape({
				total: number,
				new: number,
				acknowledged: number,
				resolved: number,
				highestSeverity: string
			}),
			status: string
		}),
		loading: bool,
		isConnected: bool,
		connectionState: string
	})
};

export default ListApp;
